'use client';

import { c as _c } from "react/compiler-runtime";
import type { Address } from 'viem';
import { useCallback } from 'react';
import { useCapabilities } from 'wagmi';
import { useUserAccount } from '../providers/UserAccountProvider';
import { SAFE_CONNECTOR_ID, supportsAtomicBatching } from '../utils';

/**
 * The one place the atomic-batching capability boundary lives: EIP-5792
 * `atomic.status` for every wallet, plus the legacy `atomicBatch` shape only
 * for the Safe Apps connector — not `useIsSafeAccount`, which also matches
 * Safes reached via WalletConnect and must not get the legacy opt-in.
 *
 * Returns the wagmi query result; `data` is `undefined` while unsettled
 * (loading, not yet `enabled`, or errored before `select` runs) so consumers
 * can distinguish "not resolved yet" from a confirmed `false`. Callers that
 * need a conservative boolean should apply `?? false` themselves.
 */
export function useWalletSupportsAtomicBatching(account, chainId, t0) {
  const $ = _c(11);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    enabled: t2
  } = t1;
  const enabled = t2 === undefined ? true : t2;
  const {
    connector
  } = useUserAccount();
  const acceptLegacyAtomicBatch = connector?.id === SAFE_CONNECTOR_ID;
  let t3;
  if ($[2] !== acceptLegacyAtomicBatch) {
    t3 = capabilities => supportsAtomicBatching(capabilities, {
      acceptLegacyAtomicBatch
    });
    $[2] = acceptLegacyAtomicBatch;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const select = t3;
  const t4 = enabled && !!account;
  let t5;
  if ($[4] !== select || $[5] !== t4) {
    t5 = {
      select,
      retry: 0,
      retryDelay: 0,
      enabled: t4
    };
    $[4] = select;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== account || $[8] !== chainId || $[9] !== t5) {
    t6 = {
      account,
      chainId,
      query: t5
    };
    $[7] = account;
    $[8] = chainId;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return useCapabilities(t6);
}